<template>
  <img ref="img" :src="imgSrc" :alt="computedAlt" v-bind="$attrs" v-if="showImage" v-show="!blurhash || imageLoaded" :width="imageWidth" :height="imageHeight" />
  <canvas ref="canvas" v-if="blurhash && !imageLoaded" v-bind="$attrs" :style="canvasStyle" :width="canvasWidth" :height="canvasHeight" />
</template>

<script>
import { ref, toRefs, computed, watchEffect, nextTick, onMounted, onBeforeUnmount } from 'vue'
import imageSizeUrl from '../utils/imageSizeUrl'
import setupCanvasBlurhash from '../utils/setupCanvasBlurhash'
import getImageSize from '../utils/imageSizeCalc'
import imagesloaded from 'imagesloaded'
import supportWebp from '../utils/supportWebp'
import * as observer from '../utils/AwImgObserver.js'

export default {
  props: {
    image: Object,
    size: {
      type: String,
      default: 'original'
    },
    src: {
      type: String,
      default: null
    },
    alt: {
      type: String,
      default: null
    }
  },
  setup (props, { attrs }) {

    const img = ref(null)
    const canvas = ref(null)

    const { image, src, size, alt } = toRefs(props)

    const computedAlt = computed(() => {
      return alt.value ?? image.value?.alt ?? ''
    })

    const blurhash = computed(() => {
      return image.value?.blurhash
    })

    const imgSrc = computed(() => {
      let srcUrl = image.value ? imageSizeUrl(image.value, size.value, computedAlt.value) : src.value
      
      if (supportWebp && srcUrl.substr(0, 1) === '/' && srcUrl.substr(0, 2) !== '//') {
        const ext = srcUrl.substr(-4)
  
        if (['.gif', '.svg', 'webp'].indexOf(ext) === -1) {
          srcUrl = srcUrl.replace(ext + '/', ext + '.webp/') + '.webp'
        }
      }
      return srcUrl
    })

    const imageLoaded = ref(false)
    const loadImage = ref(false)
    
    const showImage = computed(() => {
      return loadImage.value || !image.value?.blurhash
    })

    const imageSize = computed(() => {
      if (!image.value) return
      return getImageSize(image.value, size.value)
    })

    const imageWidth = computed(() => {
      if (!imageSize.value) return attrs.width || undefined
      return imageSize.value[0]
    })

    const imageHeight = computed(() => {
      if (!imageSize.value) return attrs.height || undefined
      return imageSize.value[1]
    })

    const canvasWidth = computed(() => {
      return imageHeight.value && imageWidth.value ? 64 : 32
    })

    const canvasHeight = computed(() => {
      if (imageHeight.value && imageWidth.value) {
        return Math.round(imageHeight.value / imageWidth.value * 64)
      }

      return 32
    })

    const canvasStyle = computed(() => {
      if (!imageSize.value) return

      return {
        maxWidth: imageSize.value[0] + 'px',
        maxHeight: imageSize.value[1] + 'px',
        width: '100%',
        height: 'auto',
      }
    })

    const doLoadImage = () => {
      nextTick(() => {
        const imgLoaded = imagesloaded(img.value)
        imgLoaded.on('done', () => {
          imageLoaded.value = true
        })
      })
    }


    onMounted(() => {
      if (!canvas.value) {
        return
      }

      observer.registry(canvas.value, () => {
        loadImage.value = true
        doLoadImage()
      })
  
    })

    onBeforeUnmount(() => {
      if (!canvas.value) {
        return
      }
      
      observer.unregistry(canvas.value)
    })

    watchEffect(() => {
      if (!canvas.value) {
        return
      }

      if (!blurhash.value) {
        return
      }

      setupCanvasBlurhash(canvas.value, image.value)
    })


    return {
      img,
      canvas,
      computedAlt,
      blurhash,
      imgSrc,
      imageLoaded,
      loadImage,
      showImage,
      imageWidth,
      imageHeight,
      canvasWidth,
      canvasHeight,
      canvasStyle
    }
  },
}
</script>